import React, { useRef, useEffect, useState, useMemo } from 'react';
import { FilmIcon } from '../icons/FilmIcon';
import { PlayIcon } from '../icons/PlayIcon';
import { MagicWandIcon } from '../icons/MagicWandIcon';
import { RenderModal } from './RenderModal';

const PauseIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
    </svg>
);

export const PreviewMonitor = ({ project, playheadPosition, isPlaying, setIsPlaying, setPlayheadPosition, onProjectUpdate }) => {
    const videoRef = useRef(null);
    const audioRefs = useRef({});
    const requestRef = useRef(0);
    const timeline = project.timeline;

    const [activeVideoClip, setActiveVideoClip] = useState(null);
    const [isRenderModalOpen, setIsRenderModalOpen] = useState(false);
    const [videoObjectUrl, setVideoObjectUrl] = useState(null);

    const audioAssetMap = useMemo(() => {
        const map = new Map();
        for (const asset of project.assets) {
            if (asset.type === 'audio' && asset.data) {
                map.set(asset.id, asset.data);
            }
        }
        return map;
    }, [project.assets]);

    const activeTextClips = useMemo(() => {
        if (!timeline) return [];
        return timeline.clips.filter(c => 
            c.type === 'text' && 
            playheadPosition >= c.start && 
            playheadPosition < c.start + c.duration
        );
    }, [timeline, playheadPosition]);
    
    const activeAudioClips = useMemo(() => {
         if (!timeline) return [];
        return timeline.clips.filter(c => 
            c.type === 'audio' && 
            playheadPosition >= c.start && 
            playheadPosition < c.start + c.duration
        );
    }, [timeline, playheadPosition]);
    
    const currentShot = useMemo(() => {
        if (!activeVideoClip) return null;
        return project.scenes.flatMap(s => s.shots).find(s => s.id === activeVideoClip.shotId);
    }, [activeVideoClip, project.scenes]);

    useEffect(() => {
        let isMounted = true;
        let tempUrl = null;
        const src = currentShot?.video;

        if (src && src.startsWith('data:')) {
            fetch(src)
                .then(res => res.blob())
                .then(blob => {
                    if (isMounted) {
                        tempUrl = URL.createObjectURL(blob);
                        setVideoObjectUrl(tempUrl);
                    }
                })
                .catch(error => {
                    console.error("Error converting data URL to blob URL in PreviewMonitor:", error);
                    if (isMounted) setVideoObjectUrl(src);
                });
        } else {
            setVideoObjectUrl(src || null);
        }

        return () => {
            isMounted = false;
            if (tempUrl) URL.revokeObjectURL(tempUrl);
        };
    }, [currentShot?.video]);


    useEffect(() => {
        const currentClip = timeline?.clips.find(c =>
            c.type === 'video' && playheadPosition >= c.start && playheadPosition < c.start + c.duration
        );

        if (currentClip?.id !== activeVideoClip?.id) {
            setActiveVideoClip(currentClip || null);
            return;
        }

        const videoEl = videoRef.current;
        const activeAudioClipIds = new Set(activeAudioClips.map(c => c.id));
        const speed = currentClip?.speed || 1;
        
        if (isPlaying) {
            if (currentShot?.video && videoEl) {
                if (videoEl.playbackRate !== speed) {
                    videoEl.playbackRate = speed;
                }

                const relativeTime = (playheadPosition - (currentClip?.start || 0));
                const videoTime = (relativeTime * speed) + (currentClip?.sourceStart || 0);
                
                if (Math.abs(videoEl.currentTime - videoTime) > 0.25) videoEl.currentTime = videoTime;
                
                if (videoEl.paused) {
                    if (videoEl.readyState >= 2) { 
                        const playPromise = videoEl.play();
                        if (playPromise !== undefined) {
                            playPromise.catch(e => console.error("Video play failed", e));
                        }
                    }
                }
            } else if (videoEl && !videoEl.paused) {
                videoEl.pause();
            }

            for (const clipId of Object.keys(audioRefs.current)) {
                const audioEl = audioRefs.current[clipId];
                if (!audioEl) continue;

                if (activeAudioClipIds.has(clipId)) {
                    const clip = activeAudioClips.find(c => c.id === clipId);
                    audioEl.volume = clip.volume;
                    const audioTime = playheadPosition - clip.start;
                    if (Math.abs(audioEl.currentTime - audioTime) > 0.2) audioEl.currentTime = audioTime;
                    if (audioEl.paused) audioEl.play().catch(e => console.error(`Audio play failed for ${clipId}`, e));
                } else {
                    if (!audioEl.paused) audioEl.pause();
                }
            }
        } else {
            if (videoEl && !videoEl.paused) videoEl.pause();
            
            if (currentShot?.video && videoEl && !isPlaying) {
                 const relativeTime = (playheadPosition - (currentClip?.start || 0));
                 const videoTime = (relativeTime * speed) + (currentClip?.sourceStart || 0);
                 if (Math.abs(videoEl.currentTime - videoTime) > 0.1) videoEl.currentTime = videoTime;
            }

            for (const audioEl of Object.values(audioRefs.current)) {
                if (audioEl instanceof HTMLAudioElement && !audioEl.paused) {
                    audioEl.pause();
                }
            }
        }

    }, [playheadPosition, isPlaying, timeline, activeVideoClip, currentShot, activeAudioClips]);


    const animate = () => {
        setPlayheadPosition(prev => {
            if (prev >= (timeline?.duration ?? 0)) {
                setIsPlaying(false);
                return timeline?.duration ?? 0;
            }
            return prev + 1 / 60;
        });
        requestRef.current = requestAnimationFrame(animate);
    };

    useEffect(() => {
        if (isPlaying) {
            requestRef.current = requestAnimationFrame(animate);
        } else {
            cancelAnimationFrame(requestRef.current);
        }
        return () => cancelAnimationFrame(requestRef.current);
    }, [isPlaying, timeline?.duration]);

    const handleSeek = (e) => {
        const newPos = parseFloat(e.target.value);
        setPlayheadPosition(newPos);
    };

    const getTransitionStyle = () => {
        if (!activeVideoClip || !activeVideoClip.transition || activeVideoClip.transition.type === 'none') {
            return { opacity: 1 };
        }

        const { type, duration } = activeVideoClip.transition;
        const timeIntoClip = playheadPosition - activeVideoClip.start;
        const timeUntilEnd = (activeVideoClip.start + activeVideoClip.duration) - playheadPosition;

        if (type === 'fade-in') {
            if (timeIntoClip < duration) {
                return { opacity: timeIntoClip / duration };
            }
        } else if (type === 'fade-out') {
            if (timeUntilEnd < duration) {
                return { opacity: timeUntilEnd / duration };
            }
        } else if (type === 'cross-dissolve') {
            if (timeIntoClip < duration) {
                return { opacity: timeIntoClip / duration };
            } else if (timeUntilEnd < duration) {
                return { opacity: timeUntilEnd / duration };
            }
        }
        return { opacity: 1 };
    };

    return (
        <div className="flex-grow bg-black flex flex-col items-center justify-center p-4 relative min-h-0">
             {timeline?.clips.filter(c => c.type === 'audio').map((clip) => {
                const audioSrc = audioAssetMap.get(clip.assetId);
                return audioSrc ? <audio key={clip.id} ref={el => { if (el) audioRefs.current[clip.id] = el }} src={audioSrc} crossOrigin="anonymous"/> : null;
            })}

            <div className="w-full h-full aspect-video bg-black relative flex items-center justify-center">
                {currentShot?.video ? (
                    <video 
                        ref={videoRef} 
                        src={videoObjectUrl || ''} 
                        className="w-full h-full object-contain transition-opacity duration-75" 
                        style={getTransitionStyle()}
                        crossOrigin="anonymous" 
                        playsInline 
                        muted={false} 
                    />
                ) : currentShot?.startFrameImage ? (
                    <img 
                        src={currentShot.startFrameImage} 
                        className="w-full h-full object-contain transition-opacity duration-75" 
                        style={getTransitionStyle()}
                        alt="Shot Preview" 
                    />
                ) : (
                     <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-600">
                        <FilmIcon className="w-24 h-24" />
                        <p>Timeline is empty or playhead is on a gap</p>
                    </div>
                )}
                 {activeTextClips.map(clip => (
                    <div 
                        key={clip.id}
                        className="absolute text-center p-4 pointer-events-none"
                        style={{
                            color: clip.color,
                            fontSize: `${clip.fontSize}px`,
                            textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
                        }}
                    >
                        {clip.content}
                    </div>
                 ))}
            </div>

            <div className="w-full max-w-4xl absolute bottom-4 left-1/2 -translate-x-1/2 p-2 bg-black/50 rounded-lg flex items-center space-x-4">
                <button onClick={() => setIsPlaying(!isPlaying)} className="text-white p-2">
                    {isPlaying ? <PauseIcon className="w-6 h-6" /> : <PlayIcon className="w-6 h-6" />}
                </button>
                <span className="text-xs w-16 text-center">{playheadPosition.toFixed(2)}s</span>
                <input
                    type="range"
                    min="0"
                    max={timeline?.duration || 100}
                    step="0.01"
                    value={playheadPosition}
                    onChange={handleSeek}
                    className="w-full"
                />
                 <span className="text-xs w-16 text-center">{timeline?.duration.toFixed(2)}s</span>
                 <button 
                    onClick={() => setIsRenderModalOpen(true)} 
                    className="text-white p-2 rounded-full hover:bg-purple-600/50 transition-colors" 
                    title="Render video"
                >
                    <MagicWandIcon className="w-6 h-6" />
                </button>
            </div>
            
            {isRenderModalOpen && (
                <RenderModal
                    project={project}
                    onClose={() => setIsRenderModalOpen(false)}
                    onProjectUpdate={onProjectUpdate}
                />
            )}
        </div>
    );
};
